// Transfer contract for "Adopt This Strategy" in the alpha analyzer: the
// payload rides one navigation to /alpha/dashboard as route state, where the
// dashboard seeds it into the alpha allocation draft and opens the allocations
// editor. An unedited Plutus Managed seed is one MANAGED_STRATEGY row; an
// edited sleeve or a self-built mix is the independent PORTFOLIO/BASKET lines.
// Route state is consumed one-shot and stripped from history, so back or
// refresh can never re-adopt.

export const ALPHA_ADOPT_MIX_STATE_KEY = "alphaAdoptWhatIfMix" as const;

const ADOPT_ENTITY_TYPES = ["PORTFOLIO", "BASKET", "MANAGED_STRATEGY"] as const;

export interface AlphaAdoptMixEntry {
  entityType: (typeof ADOPT_ENTITY_TYPES)[number];
  productId: number;
  // Percent, sign-carrying by contract; alpha is long-only so >= 0.
  weight: number;
  // Optional label; the allocation draft resolves empty names from its catalog.
  displayName: string;
}

interface AlphaAdoptMixRoutePayload {
  effectiveUserId: string;
  entries: AlphaAdoptMixEntry[];
}

// Unedited Plutus Managed What-If seed -> one MANAGED_STRATEGY row.
// Edited sleeves or a self-built mix -> the independent mix lines as supplied.
export function resolveAlphaAdoptMixEntries(input: {
  whatIfSource: string;
  mixDirty: boolean;
  managedStrategyId: number | null;
  managedDisplayName: string;
  mixEntries: AlphaAdoptMixEntry[];
}): AlphaAdoptMixEntry[] {
  if (
    input.whatIfSource === "managed"
    && !input.mixDirty
    && input.managedStrategyId != null
    && Number.isInteger(input.managedStrategyId)
    && input.managedStrategyId > 0
  ) {
    return [{
      entityType: "MANAGED_STRATEGY",
      productId: input.managedStrategyId,
      weight: 100,
      displayName: input.managedDisplayName,
    }];
  }
  return input.mixEntries;
}

export function buildAlphaAdoptMixRouteState(
  effectiveUserId: string,
  entries: AlphaAdoptMixEntry[]
): Record<string, unknown> {
  const payload: AlphaAdoptMixRoutePayload = { effectiveUserId, entries };
  return { [ALPHA_ADOPT_MIX_STATE_KEY]: payload };
}

function isValidEntry(value: unknown): value is AlphaAdoptMixEntry {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const entry = value as Partial<AlphaAdoptMixEntry>;
  return ADOPT_ENTITY_TYPES.some((t) => t === entry.entityType)
    && Number.isInteger(entry.productId)
    && (entry.productId as number) > 0
    && typeof entry.weight === "number"
    && Number.isFinite(entry.weight)
    && entry.weight >= 0
    && typeof entry.displayName === "string";
}

// Route state is untrusted input (hand-crafted history entries, older builds).
// Anything malformed means "no adopt" rather than seeding the draft with it.
export function getAlphaAdoptMixFromRouteState(
  state: unknown,
  effectiveUserId: string | null | undefined
): AlphaAdoptMixEntry[] | null {
  if (!effectiveUserId) return null;
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;
  const payload = (state as Record<string, unknown>)[ALPHA_ADOPT_MIX_STATE_KEY];
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const { effectiveUserId: payloadUserId, entries } = payload as Partial<AlphaAdoptMixRoutePayload>;
  if (payloadUserId !== effectiveUserId) return null;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  if (!entries.every(isValidEntry)) return null;
  return entries.map((entry) => ({
    entityType: entry.entityType,
    productId: entry.productId,
    weight: entry.weight,
    displayName: entry.displayName,
  }));
}

export function hasAlphaAdoptMixRouteState(state: unknown): boolean {
  return Boolean(
    state
    && typeof state === "object"
    && !Array.isArray(state)
    && Object.prototype.hasOwnProperty.call(state, ALPHA_ADOPT_MIX_STATE_KEY)
  );
}

// Invalid, legacy-unbound, or another account's handoff must be removed before
// any Alpha route renders account-owned UI.
export function shouldDiscardAlphaAdoptMixRouteState(
  state: unknown,
  effectiveUserId: string | null | undefined
): boolean {
  return hasAlphaAdoptMixRouteState(state)
    && getAlphaAdoptMixFromRouteState(state, effectiveUserId) === null;
}

// The same route state without the adopt key (other keys carried through);
// null when nothing else remains, matching react-router's empty state.
export function stripAlphaAdoptMixRouteState(state: unknown): unknown {
  if (!state || typeof state !== "object" || Array.isArray(state)) return state ?? null;
  const { [ALPHA_ADOPT_MIX_STATE_KEY]: _adopt, ...rest } = state as Record<string, unknown>;
  return Object.keys(rest).length > 0 ? rest : null;
}
